import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProjectTeamMember {
  assignment_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  display_name: string | null;
  email: string;
  profile_photo_url: string | null;
  role_name: string | null;
  role_color: string | null;
  base_level: number | null;
}

export const useProjectTeam = (projectId: string | undefined) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["project_team", projectId],
    queryFn: async () => {
      const { data: assignments, error } = await supabase
        .from("project_assignments")
        .select("id, user_id")
        .eq("project_id", projectId!);
      if (error) throw error;
      if (!assignments?.length) return [] as ProjectTeamMember[];

      const userIds = assignments.map((a: any) => a.user_id).filter(Boolean);
      const { data: profiles } = await supabase
        .from("user_profiles")
        .select("user_id, first_name, last_name, display_name, email, profile_photo_url, roles(name, color, base_level)")
        .in("user_id", userIds);

      const profileMap = Object.fromEntries((profiles ?? []).map((p: any) => [p.user_id, p]));

      return assignments.map((row: any) => {
        const p = profileMap[row.user_id] ?? {};
        return {
          assignment_id: row.id,
          user_id: row.user_id,
          first_name: p.first_name ?? "",
          last_name: p.last_name ?? "",
          display_name: p.display_name ?? null,
          email: p.email ?? "",
          profile_photo_url: p.profile_photo_url ?? null,
          role_name: p.roles?.name ?? null,
          role_color: p.roles?.color ?? null,
          base_level: p.roles?.base_level ?? null,
        };
      }) as ProjectTeamMember[];
    },
    enabled: !!projectId,
  });

  const addMember = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await (supabase
        .from("project_assignments") as any)
        .insert({ project_id: projectId!, user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_team", projectId] });
      qc.invalidateQueries({ queryKey: ["projects", projectId] });
    },
  });

  const removeMember = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from("project_assignments")
        .delete()
        .eq("id", assignmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_team", projectId] });
      qc.invalidateQueries({ queryKey: ["projects", projectId] });
    },
  });

  return {
    members: query.data ?? [],
    isLoading: query.isLoading,
    addMember,
    removeMember,
  };
};
